<script lang="ts">
  import FieldTypeIcon from '../../components/FieldTypeIcon.svelte';
  import Icon from '../../components/Icon.svelte';
  import type { ApiField } from '../data-tables-api';
  import { fieldType } from '../data-tables-catalog';
  import { ApiError } from '../api';
  import { session } from '../session';
  import { canEditField, type Role } from '../roles';
  import FieldDisplay from './FieldDisplay.svelte';
  import FieldEditor from './FieldEditor.svelte';

  /** Маппа validate.ts error codes у людино-читані повідомлення. */
  const ERROR_MESSAGES: Record<string, string> = {
    field_not_defined: 'Поле не визначено у структурі',
    field_readonly: 'Обчислюється системою — не редагується',
    required: 'Обовʼязкове поле',
    invalid_email: 'Невалідний email',
    invalid_phone: 'Невалідний номер телефону',
    invalid_url: 'Невалідний URL',
    invalid_country: 'Очікується ISO-код країни (2-5 літер)',
    invalid_number: 'Очікується число',
    invalid_percent: 'Очікується число (відсоток)',
    invalid_currency: 'Очікується сума',
    invalid_date: 'Невалідна дата',
    invalid_datetime: 'Невалідна дата/час',
    value_not_in_options: 'Значення не у списку допустимих',
    invalid_checkbox: 'Очікується true/false',
    invalid_tags: 'Очікується масив тегів',
    dedup_conflict: 'Запис з таким значенням вже існує',
  };

  /** Витягнути per-field error з ApiError.details (формат validate.ts). */
  function extractFieldError(err: unknown, fieldKey: string): string | null {
    if (!(err instanceof ApiError)) return null;
    const details = err.details as { fields?: Record<string, string> } | undefined;
    const code = details?.fields?.[fieldKey];
    if (!code) return null;
    return ERROR_MESSAGES[code] ?? code;
  }

  let {
    field,
    value,
    onSave,
    readOnly = false,
    showLabel = true,
    record,
  }: {
    field: ApiField;
    value: unknown;
    onSave: (next: unknown) => Promise<void>;
    readOnly?: boolean;
    /** Якщо false — рендерить тільки value/editor без label-блоку (для inline-вкладень). */
    showLabel?: boolean;
    /** Запис цілком — потрібен для type='formula' substitution. Передається до FieldDisplay. */
    record?: Record<string, unknown>;
  } = $props();

  let editing = $state(false);
  let draft = $state<unknown>(undefined);
  let error = $state<string | null>(null);
  let busy = $state(false);

  // Computed/protected-readonly + formula поля не редагуються; також если readOnly=true.
  // Phase 4: per-field permissions — заборона редагування якщо роль не дотягує до min_edit_role.
  const currentRole = $derived(($session?.role ?? null) as Role | null);
  const isLocked = $derived(
    readOnly ||
      !!field.is_computed ||
      field.type === 'formula' ||
      field.type === 'stat' ||
      !canEditField(currentRole, field.min_edit_role, field.min_view_role),
  );

  function startEdit() {
    if (isLocked) return;
    draft = value;
    error = null;
    editing = true;
  }

  function cancel() {
    editing = false;
    draft = undefined;
    error = null;
  }

  async function commit() {
    if (busy) return;
    busy = true;
    error = null;
    try {
      await onSave(draft);
      editing = false;
      draft = undefined;
    } catch (e) {
      // Якщо це ApiError з validate.ts → спробуємо витягти per-field error.
      const fieldErr = extractFieldError(e, field.fieldKey);
      if (fieldErr) {
        error = fieldErr;
      } else if (e instanceof ApiError && e.code === 'dedup_conflict') {
        error = ERROR_MESSAGES.dedup_conflict;
      } else {
        error = e instanceof Error ? e.message : String(e);
      }
    } finally {
      busy = false;
    }
  }

  const ft = $derived(fieldType(field.type));
</script>

<div class="flex items-start gap-2.5 rounded-md px-2 py-1.5 mb-0.5 hover:bg-[var(--color-bg-3)]/40 group">
  <span class="grid h-5 w-5 shrink-0 place-items-center rounded text-[11px] font-bold text-[var(--color-text-muted-2)] mt-0.5"><FieldTypeIcon type={ft.key} size={14} /></span>
  <div class="flex-1 min-w-0">
    {#if showLabel}
      <div class="flex items-center gap-1.5">
        <div class="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">{field.label}</div>
        {#if field.is_computed}
          <span class="inline-flex text-[var(--color-text-muted)]" title={field.computation?.description ?? 'Обчислюється системою'}><Icon name="refresh" size={11} /></span>
        {/if}
      </div>
    {/if}

    {#if editing}
      <div class="mt-1">
        <FieldEditor {field} value={draft} onChange={(v) => (draft = v)} {error} />
        <div class="mt-1 flex gap-1">
          <button
            type="button"
            onclick={commit}
            disabled={busy}
            class="inline-flex items-center rounded bg-[var(--color-pu)] px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-50"
          >
            {#if busy}…{:else}<Icon name="check" size={13} />{/if}
          </button>
          <button
            type="button"
            onclick={cancel}
            disabled={busy}
            class="inline-flex items-center rounded border border-[var(--color-border-3)] bg-white px-2 py-0.5 text-[10px] font-semibold text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
          >
            <Icon name="close" size={13} />
          </button>
        </div>
      </div>
    {:else}
      <div class="flex items-center gap-1 text-[12.5px]">
        <div class="flex-1 min-w-0 truncate">
          <FieldDisplay {field} {value} {record} />
        </div>
        {#if !isLocked}
          <button
            type="button"
            onclick={startEdit}
            class="inline-flex items-center opacity-0 group-hover:opacity-100 transition-opacity rounded px-1 py-0.5 text-[var(--color-pu-3)] hover:bg-purple-100"
            title="Редагувати"
            aria-label="Редагувати {field.label}"
          >
            <Icon name="edit" size={12} />
          </button>
        {/if}
      </div>
      {#if error}
        <div class="mt-1 text-[10.5px] text-red-600">{error}</div>
      {/if}
    {/if}
  </div>
</div>
